import React, { FunctionComponent, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useBlogQuery, useCartCreateMutation, useCartDiscountCodesUpdateMutation, useCartLinesAddMutation, useCartLinesRemoveMutation, useCartQuery } from '../generated/graphql'
import { useCartId } from '../hooks/use-cart-id'
import { SvgLeftAngle, SvgRightAngle } from './svg'
import { CartProductItem } from './cart-product-item'
import { formatDecimal } from '../utils/format-decimal'
import classNames from 'classnames'
import SvgLogoIcon from '../generated/svg/logo-icon'
import dayjs from 'dayjs'
import { useCartNoteUpdateMutation } from '../generated/graphql'
import { VIPAppSDK } from '@builtbystack/vip-theme-sdk'
import { useSettings } from '../hooks/use-settings'

const Outer = styled.div`
  position: fixed;
  top: var(--hide-header-height, var(--header-height));
  height: calc(100% - var(--hide-header-height, var(--header-height)));
  width: 100%;
  transition: top 0.4s, height 0.4s;
  z-index: 1001;
  &.hide {
    pointer-events: none;
  }
`

const Bg = styled.div`
  background-color: #0002;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100% + var(--hide-header-height, var(--header-height)));
  cursor: pointer;
  transition: backdrop-filter 0.4s, background-color 0.4s;
  margin-top: calc(var(--hide-header-height, var(--header-height)) * -1);
  transition: margin-top 0.4s, height 0.4s;
  ${Outer}.hide & {
    backdrop-filter: none;
    background-color: transparent;
  }
`

const Inner = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  max-width: 22.8125rem;
  background-color: white;
  color: var(--black);
  padding: var(--page-inside-margin);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  transition: opacity 0.4s, transform 0.4s;
  ${Outer}.hide & {
    opacity: 0;
    transform: translateX(100%);
  }
`

const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin: -0.5rem;
`

const TopButton = styled.div`
  font-size: 0.625rem;
  letter-spacing: 0.1em;
  font-weight: 400;
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  cursor: pointer;
  align-items: center;
  svg {
    display: block;
    height: 1em;
    width: auto;
  }
`

const List = styled.div`
  margin-top: 6.25rem;
`

const Item = styled.div`
  & + & {
    margin-top: 1.5rem;
  }
`

const NoItem = styled.div`
  font-size: 0.875rem;
  text-align: center;
`

const TotalPriceContainer = styled.div`
margin-top: 2.5625rem;
`

const TotalPriceLine = styled.div`
  font-size: 0.9375rem;
  line-height: 1.75;
  font-weight: 400;
  font-family: var(--serif-font);
  padding: 0 var(--page-inside-margin);
  display: flex;
  justify-content: space-between;
`

const TotalPriceBar = styled.div`
  background-color: currentColor;
  height: 0.5px;
  width: 100%;
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
`

const Button = styled.a`
  font-size: 1rem;
  font-family: var(--serif-font);
  padding: 1rem;
  color: var(--black);
  border: 1px solid var(--black);
  background-color: var(--black);
  color: white;
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: baseline;
  margin-top: 0.8125rem;
  transition: background-color 0.4s, color 0.4s;
  &:hover {
    background-color: white;
    color: var(--black);
  }
  span {
    display: block;
    transform: translateY(-0.0625em);
  }
  svg {
    display: block;
    height: 0.75em;
    margin-block: calc(0.25em / 2);
    width: auto;
  }
`

const Logo = styled.div`
margin-top: auto;
padding-top: 2rem;
  svg {
    display: block;
    height: 2.625rem;
    width: auto;
    margin-left: auto;
    margin-right: auto;
  }
`

const DatePickerContainer = styled.div`
margin-top: 2rem;
`

const DatePickerLabel = styled.div`
  font-size: 0.9375rem;
  line-height: 1.75;
  font-weight: 400;
  margin-top: 0.5rem;
  font-family: var(--serif-font);
`

const DatePickerInput = styled.div`
  display: flex;
  gap: 0.5rem;
  input, select {
    color: inherit;
    border: 1px solid currentColor;
    padding: 0.5rem 1rem;
    display: block;
    box-sizing: border-box;
    font-size: 1rem;
    line-height: inherit;
    &:focus-visible {
      outline: none;
    }
  }
  .react-datepicker-wrapper {
    flex: 1 1 auto;
    input {
      width: 100%;
    }
  }
  option {
    line-height: inherit;
  }
`

const SelectContainer = styled.div`
  position: relative;
  svg {
    display: block;
    height: 0.5rem;
    width: auto;
    transform: translateY(-0.125rem) rotate(90deg);
    position: absolute;
    right: 0.75rem;
    top: 0;
    bottom: 0;
    margin-block: auto;
    pointer-events: none;
  }
  select {
    appearance: none;
    padding-right: 1.5rem;
  }
`

const NoticesContainer = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-inline: var(--page-inside-margin);
`

const NoticeItem = styled.div`
  font-size: 0.8rem;
  line-height: 1.5;
  color: #888;
`

const PointContainer = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border: 1px solid #ccc;
`

const PointHeader = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--black);
`

const PointBalance = styled.div`
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
  color: #666;
`

const PointInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`

const PointInput = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  &:focus {
    outline: none;
    border-color: var(--black);
  }
`

const PointButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: var(--black);
  color: white;
  border: none;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.3s;
  &:hover {
    background-color: #333;
  }
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`

const PointUsed = styled.div`
  font-size: 0.875rem;
  color: #d32f2f;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PointCancelButton = styled.button`
  padding: 0.25rem 0.5rem;
  background-color: transparent;
  color: #d32f2f;
  border: 1px solid #d32f2f;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.3s;
  &:hover {
    background-color: #d32f2f;
    color: white;
  }
`

const LoginButton = styled.div`
  font-size: 0.8rem;
  margin-top: 2rem;
  cursor: pointer;
  text-decoration: underline;
`

const PointNote = styled.div`
  font-size: 0.75rem;
  color: #888;
  margin-top: 0.5rem;
`

type ProductSplitConfig = {
    targetTags: string[];
    splitProducts: {
        merchandiseId: string;
        sellingPlanId: string;
    }[];
    discountCode: string;
}
const productSplitConfigs: ProductSplitConfig[] = [
  // {
  //   targetTags: ['perfect-set', 'subscription'],
  //   splitProducts: [
  //     {
  //       merchandiseId: `gid://shopify/ProductVariant/${46451541147880}`,
  //       sellingPlanId: `gid://shopify/SellingPlan/${4712562920}`
  //     },
  //     {
  //       merchandiseId: `gid://shopify/ProductVariant/${46377838248168}`,
  //       sellingPlanId: `gid://shopify/SellingPlan/${4934697192}`
  //     },
  //     {
  //       merchandiseId: `gid://shopify/ProductVariant/${46205681139944}`,
  //       sellingPlanId: `gid://shopify/SellingPlan/${4934664424}`
  //     }
  //   ],
  //   discountCode: 'perfect-set-3cart'
  // }
]

const rankPointTimesMap: { [key: string]: number } = {
  Basic: 1,
  Gold: 2,
  'Pink Gold': 3,
  Platinum: 4,
  Diamond: 5,
  Black: 10
}

type CartProps = {

}
export const Cart: FunctionComponent<CartProps> = () => {
  console.log('Cart component rendering')
  const [show, setShow] = useState(false)
  const {customer} = useSettings()
  const [pointBalance, setPointBalance] = useState<number>(0)
  const [pointInput, setPointInput] = useState<string>('')
  const [usedPoints, setUsedPoints] = useState<number>(0)
  const [pointDiscountCode, setPointDiscountCode] = useState<string>('')
  
  const isLoggedIn = !!customer
  
  const noticesQuery = useBlogQuery({
    variables: {
      handle: 'notices'
    }
  })
  const cartId = useCartId()
  const cartQuery = useCartQuery({
    skip: !cartId,
    variables: {
      id: cartId!
    }
  })
  const cart = cartQuery.data?.cart
  const lines = cart?.lines.nodes ?? []
  const [cartNoteUpdateMutation] = useCartNoteUpdateMutation()
  const [cartLinesRemoveMutation] = useCartLinesRemoveMutation()
  const [cartLinesAddMutation] = useCartLinesAddMutation()
  const [cartDiscounsCodesUpdateMutation] = useCartDiscountCodesUpdateMutation()

  console.log('cartLinesAddMutation:', cartLinesAddMutation)

  const sdk = new VIPAppSDK(typeof window !== 'undefined' ? window.location.origin : '')

  useEffect(() => {
  }, [])

  useEffect(() => {
    const count = lines.map(line => line.quantity).reduce((a, b) => a + b, 0)
    window.cart?.dispatchEvent(new CustomEvent('count-updated', { detail: { count } }))
  }, [lines])

  useEffect(() => {
    const cartEventTarget = new EventTarget() as Cart

    cartEventTarget.open = () => {
      setShow(true)
    }

    cartEventTarget.close = () => {
      setShow(false)
    }

    cartEventTarget.toggle = () => {
      setShow(v => !v)
    }

    cartEventTarget.add = async (variantId: string, quantity?: number, sellingPlanId?: string) => {
      try {
        if (!window.cartId) return
        const resolvedCartId = await window.cartId
        if (!resolvedCartId) return

        await cartLinesAddMutation({
          variables: {
            cartId: resolvedCartId,
            lines: [{
              merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
              quantity: quantity ?? 1,
              sellingPlanId: sellingPlanId ? `gid://shopify/SellingPlan/${sellingPlanId}` : null
            }]
          }
        })

        cartEventTarget.open()
      } catch (err) {
        console.error('Add to cart failed:', err)
      }
    }

    window.cart = cartEventTarget
  }, [cartLinesAddMutation])

  useEffect(() => {
    const fetchPointBalance = async () => {
      if (!isLoggedIn) {
        setPointBalance(0)
        return
      }
      
      try {
        const { approvedPoint } = await sdk.customerApprovedPoint()
        setPointBalance(approvedPoint)
      } catch (error) {
        setPointBalance(0)
      }
    }
    
    if (show) {
      fetchPointBalance()
    }
  }, [show, sdk, isLoggedIn])

  const usePoints = async () => {
    const points = parseInt(pointInput)
    if (isNaN(points) || points <= 0 || points > pointBalance) {
      alert('有効なマイル数を入力してください')
      return
    }

    try {
      const { point, discountCode } = await sdk.customerPointUse({ point: points })
      setUsedPoints(point)
      setPointDiscountCode(discountCode)
      setPointBalance(prev => prev - point)
      setPointInput('')
      
      // カートにディスカウントコードを適用
      if (cartId) {
        await cartDiscounsCodesUpdateMutation({
          variables: {
            cartId,
            discountCodes: [discountCode]
          }
        })
      }
    } catch (error) {
      console.error('マイル利用に失敗しました:', error)
      alert('マイル利用に失敗しました')
    }
  }

  // マイル利用キャンセル
  const cancelPoints = async () => {
    try {
      await sdk.customerPointCancel()
      setPointBalance(prev => prev + usedPoints)
      setUsedPoints(0)
      setPointDiscountCode('')
      
      // カートからディスカウントコードを削除
      if (cartId) {
        await cartDiscounsCodesUpdateMutation({
          variables: {
            cartId,
            discountCodes: []
          }
        })
      }
    } catch (error) {
      console.error('マイル利用キャンセルに失敗しました:', error)
      alert('マイル利用キャンセルに失敗しました')
    }
  }

  // ログインページへリダイレクト
  const goToLogin = () => {
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.href = `/account/login?return_url=${returnUrl}`
  }

  const processCartSplitting = async () => {
    if (!cart || !cartId) return

    for (const line of lines) {
      const tags = line.merchandise.product.tags
      const quantity = line.quantity

      // 設定に一致するものを検索
      const matchingConfig = productSplitConfigs.find(config => 
        config.targetTags.every(tag => tags.includes(tag))
      )

      if (matchingConfig) {
        // 分割商品を追加
        await cartLinesAddMutation({
          variables: {
            cartId,
            lines: matchingConfig.splitProducts.map(product => ({
              merchandiseId: product.merchandiseId,
              sellingPlanId: product.sellingPlanId,
              quantity
            }))
          }
        })

        // 元の商品を削除
        await cartLinesRemoveMutation({
          variables: {
            cartId,
            lineIds: line.id
          }
        })

        // ディスカウントコードを適用
        if (matchingConfig.discountCode) {
          await cartDiscounsCodesUpdateMutation({
            variables: {
              cartId,
              discountCodes: [matchingConfig.discountCode]
            }
          })
        }
      }
    }
  }

  const checkout = async () => {
    if (cart && cartId) {
      await processCartSplitting()
      location.href = cart?.checkoutUrl
    }
  }

  console.log('Cart component about to return JSX')
  return <Outer id="cart" className={classNames({hide: !show})}>
    <Bg onClick={() => setShow(false)}/>
    <Inner>
      <TopContainer>
        <TopButton onClick={() => setShow(false)}>
          <SvgLeftAngle size={8} />
          RETURN TO SHOP
        </TopButton>
      </TopContainer>
      <List>
        {
          lines.length < 1 ? <>
            <NoItem>カートに商品はありません</NoItem>
          </> :
          lines.map(line => {
            return <Item key={line.id}>
              <CartProductItem data={line} />
            </Item>
          })
        }
      </List>
      <TotalPriceContainer>
        <TotalPriceLine>
          <span>小計</span>
          ¥{formatDecimal(cart?.cost.subtotalAmount.amount)}
        </TotalPriceLine>
        <TotalPriceLine>
          <span>送料</span>
          無料
        </TotalPriceLine>
        <TotalPriceBar />
        <TotalPriceLine>
          <span>合計</span>
          ¥{formatDecimal(cart?.cost.totalAmount.amount)}
        </TotalPriceLine>
        {cart?.cost.totalAmount.amount && (
          <TotalPriceLine>
            <span>獲得予定マイル</span>
            {(() => {
              const totalAmount = parseFloat(cart.cost.totalAmount.amount)
              const basePoints = Math.floor(totalAmount / 110)
              const customerRank = customer?.rank || 'Basic'
              const multiplier = rankPointTimesMap[customerRank] || 1
              const earnedPoints = basePoints * multiplier
              return `${earnedPoints.toLocaleString()}`
            })()}
          </TotalPriceLine>
        )}
      </TotalPriceContainer>
      
      {isLoggedIn ? (
        <PointContainer>
          <PointHeader>マイル利用</PointHeader>
          <PointBalance>
            利用可能マイル: {pointBalance.toLocaleString()}pt
          </PointBalance>
          {usedPoints > 0 ? (
            <PointUsed>
              <span>利用中: {usedPoints.toLocaleString()}pt</span>
              <PointCancelButton onClick={cancelPoints}>
                利用を取り消す
              </PointCancelButton>
            </PointUsed>
          ) : (
            <PointInputContainer>
              <PointInput
                type="number"
                value={pointInput}
                onChange={e => setPointInput(e.target.value)}
                placeholder="利用するマイル数"
                min="1"
                max={pointBalance}
              />
              <PointButton 
                onClick={usePoints}
                disabled={!pointInput || pointBalance <= 0}
              >
                利用する
              </PointButton>
            </PointInputContainer>
          )}
          <PointNote>
            ※単品購入にのみご利用可能です
          </PointNote>
        </PointContainer>
      ) : (
        <LoginButton onClick={goToLogin}>
          ログインしてマイルを利用
        </LoginButton>
      )}

      <Button href={cart?.checkoutUrl} onClick={async e => {
        e.preventDefault()
        checkout()
      }}>
        <span>購入に進む</span>
        <SvgRightAngle size={8}/>
      </Button>
      <NoticesContainer>
        {
          noticesQuery.data?.blog?.articles.nodes.map(notice => {
            return <NoticeItem key={notice.id} dangerouslySetInnerHTML={{__html: notice.contentHtml}}/>
          })
        }
      </NoticesContainer>
      <Logo>
        <SvgLogoIcon />
      </Logo>
    </Inner>
  </Outer>
}